import React from "react"
import { format } from "date-fns"
import {
    componentStyles,
    ExpandableMarkup,
    useColors,
} from "@mallardbay/lib-react-components"

import ResponsiveBlock from "~components/shared/responsive-block"
import { useCurrentListing } from "~components/providers/current-listing-provider"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/test-ids"
import Box from "~components/shared/todo-lib-react-components/box"
import Image from "~components/shared/todo-lib-react-components/image"
import type { ListingType } from "~graphql/generated/graphql"
import { isLodgingOnlyType } from "~utils/listing-helpers"

export default function Host() {
    const { currentListing } = useCurrentListing()
    const { outfitter } = currentListing
    const styles = useStyles()

    const { name, about, created_at: createdAt, avatar } = outfitter

    const guidedByCopy = getGuidedByCopy({
        name,
        listingType: currentListing.type,
    })
    const joinedInCopy = getJoinedInCopy(createdAt)

    return (
        <ResponsiveBlock>
            <Box style={styles.contentWrapper}>
                <Box style={styles.outfitterContentColumn}>
                    <Box style={styles.header}>
                        {avatar ? (
                            <Image
                                src={avatar.url}
                                alt={avatar.description || ""}
                                style={styles.avatar}
                                testId={TEST_IDS.OUTFITTER_AVATAR}
                            />
                        ) : null}
                        <Box style={styles.titleWrapper}>
                            <Box style={styles.title}>{guidedByCopy}</Box>
                            <Box style={styles.subtext}>{joinedInCopy}</Box>
                        </Box>
                    </Box>
                    <Box style={styles.bio}>
                        <ExpandableMarkup markup={about} />
                    </Box>
                </Box>
            </Box>
        </ResponsiveBlock>
    )
}

function getJoinedInCopy(createdAt: number) {
    return `${COPY.JOINED_IN} ${format(new Date(createdAt), "MMMM yyyy")}`
}

function getGuidedByCopy({
    name,
    listingType,
}: {
    name: string
    listingType: ListingType | null | undefined
}) {
    const prefix = isLodgingOnlyType(listingType)
        ? COPY.LODGING_BY
        : COPY.GUIDED_BY

    return `${prefix} ${name}`
}

function useStyles() {
    const colors = useColors()

    return componentStyles({
        header: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: "auto",
            marginBottom: "32px",
        },
        avatar: {
            width: { base: "58px", md: "78px" },
            height: { base: "58px", md: "78px" },
            objectFit: "cover",
            borderTopLeftRadius: "50%",
            borderTopRightRadius: "50%",
            borderBottomRightRadius: "50%",
            borderBottomLeftRadius: "50%",
        },
        titleWrapper: {
            display: "flex",
            flexDirection: "column",
            height: "auto",
            justifyContent: "space-between",
            marginLeft: "16px",
        },
        title: {
            fontSize: { base: "20px", md: "24px" },
            fontWeight: 600,
        },
        subtext: {
            fontSize: { base: "14px", md: "16px" },
            fontWeight: 300,
            color: colors.contentSecondary,
            marginTop: "8px",
        },
        contentWrapper: {
            display: "flex",
            flexDirection: { base: "column", md: "row" },
            justifyContent: "space-between",
            marginBottom: "48px",
        },
        outfitterContentColumn: {
            width: "100%",
            margin: { base: "0 0 24px 0", md: "0 48px 0 0" },
        },
        bio: {
            fontSize: "16px",
            fontWeight: 300,
            lineHeight: "24px",
        },
    })
}
